// @ts-strict-ignore
import sum from 'lodash/sum';

import {Translatable} from 'common/i18n';
import {abbreviateNumber, capitalize, commaizeNumber} from 'common/util';
import {pluralize} from 'common/util/string';
import {SegmentKeyToPath} from 'common/report/retention/models/types';
import {getGroupByName, isSegmented} from 'common/report/retention/util/format';
import {RetentionQuery} from 'common/report/retention/models/retention-query';
import {TimeUnit} from 'common/widgets/date-range-picker/models/types';
import {formatDataTableNumber} from 'common/components/data-table/trend-data/utils';
import {PropertyType, SPECIAL_EVENTS} from 'common/report/constants';
import {
  Columns,
  CommonDataTableFields,
  SegmentColumnSpec,
  TableColumnType,
  TableDataType,
  TableRow,
} from 'common/components/data-table/types';
import {formatPercent as formatPercentFromCommon} from 'common/util';
import {getSegmentPropertyType, Segment} from 'common/query-builder/query-block/segment-menu/types';
import {customEventsStore} from 'common/report/stores/custom-events';
import {renameEvent} from 'common/report/util/events';

import {BirthIntervalUnit, CellType, FormattedRow, LiftRow, RetentionBookmarkParams, RetentionType} from './types';

const UNIT = {
  DAY: `day`,
  WEEK: `week`,
  MONTH: `month`,
};
const SMALLER_UNIT_MAP = {
  [UNIT.DAY]: `hour`,
  [UNIT.WEEK]: `day`,
  [UNIT.MONTH]: `week`,
};

export function getUnitSmaller(unit): string {
  return SMALLER_UNIT_MAP[unit];
}

export function getAddictionUnit(unit): string {
  // Simply becuase that is how addiction works
  return unit === UNIT.MONTH ? UNIT.DAY : getUnitSmaller(unit);
}

export function isAddiction(bookmarkParams: RetentionBookmarkParams) {
  return bookmarkParams.retention_type === `addiction`;
}

/**
 * Convert a decimal percentage to a formatted string percent to a certain precision
 * @example
 * formatPercent(0.2548, 1);
 * // 25.5%
 * formatPercent(12.342, 0);
 * // 1,234%
 **/

export function formatPercent(value: number, precision = 2): string {
  if (isNaN(value)) {
    value = 0;
  }

  return formatPercentFromCommon(value, {precision, exactPrecision: false, showSign: false});
}

/**
 * format a number to be ready to use in retention table
 * @param value number to be formatted
 * @returns {number}
 */
export function formatNumber(value: number): string {
  // In order to get an accurate count, round to 2 decimal places if the value is too small
  // Otherwise, round to the nearest integer
  const roundedValue = value < 5 ? Math.round(value * 100) / 100 : Math.round(value);
  return roundedValue < 1000000 ? commaizeNumber(roundedValue) : abbreviateNumber(roundedValue);
}

/**
 * Convert to number order given a number
 * @param number
 */

export function getRetentionBucketName({
  bucketIndex,
  retentionType,
  retentionUnit,
  dateUnit,
  bucketSizes,
}: {
  bucketIndex: number;
  retentionType: RetentionType;
  retentionUnit: TimeUnit;
  dateUnit: BirthIntervalUnit;
  bucketSizes: number[];
}): Translatable {
  return retentionType === `addiction` || !bucketSizes?.length
    ? getRetentionNonCustomBucketName({
        bucketIndex,
        retentionType,
        retentionUnit: retentionUnit || TimeUnit.Day,
        dateUnit: dateUnit || TimeUnit.Day,
      })
    : getRetentionCustomBucketName({bucketIndex, bucketSizes, retentionUnit: retentionUnit || TimeUnit.Day});
}

export function getRetentionNonCustomBucketName({
  bucketIndex,
  retentionType,
  retentionUnit,
  dateUnit,
}: {
  bucketIndex: number;
  retentionType: RetentionType;
  retentionUnit: TimeUnit;
  dateUnit: BirthIntervalUnit;
}): Translatable {
  // todo: i18n typesafety: TimeUnit is a bigger type than is supported in time-unit dictionary
  return retentionType === `addiction`
    ? // todo: i18n typesafety: getAddictionUnit needs to be typed
      ({t}) => `${bucketIndex + 2} ${t(`reports.time-unit.${getAddictionUnit(dateUnit)}` as any)}`
    : bucketIndex > 0
      ? ({t}) => `${t(`reports.time-unit.${retentionUnit}` as any)} ${bucketIndex}`
      : ({t}) => `< 1 ${t(`reports.time-unit.${retentionUnit}` as any)}`;
}

export function getRetentionCustomBucketName({
  bucketIndex,
  bucketSizes,
  retentionUnit,
}: {
  bucketIndex: number;
  bucketSizes: number[];
  retentionUnit: TimeUnit;
}) {
  const capitalizedRetentionUnit = capitalize(retentionUnit);
  const start = sum(bucketSizes.slice(0, bucketIndex));
  const end = start + bucketSizes[bucketIndex] - 1;

  return `${
    bucketIndex === 0
      ? `<${end + 1} ${pluralize(capitalizedRetentionUnit, end + 1)}`
      : `${
          bucketSizes[bucketIndex] === 1
            ? `${capitalizedRetentionUnit} ${start}`
            : `${pluralize(capitalizedRetentionUnit, true)} ${start}-${end}`
        }`
  }`;
}

export function getRetentionBucketStartAndEnd({
  bucketIndex,
  bucketSizes,
}: {
  bucketIndex: number;
  bucketSizes: number[];
}) {
  const start = sum(bucketSizes.slice(0, bucketIndex));
  const end = start + bucketSizes[bucketIndex] - 1;
  return {start, end};
}

export function getFrequencyTooltipSubtitle({
  cumulative,
  dateUnit,
  bucketIndex,
}: {
  cumulative: boolean;
  dateUnit: TimeUnit;
  bucketIndex: number;
}) {
  const count = bucketIndex + 2;
  return `${cumulative ? `Did frequency event at least` : `In minimum`} ${count} ${pluralize(
    getAddictionUnit(dateUnit),
    count,
  )} in a ${dateUnit}`;
}

export function frequencyLineChartDescription(query: RetentionQuery) {
  const {dateUnit} = query;
  const dataGroupName = `${query.getDataGroupName({plural: true})}`;

  if (!query.cumulative) {
    return `The number of ${getAddictionUnit(dateUnit)} in a ${dateUnit} ${dataGroupName} did A`;
  } else {
    return `${capitalize(dataGroupName)} did A at least X ${dateUnit === `day` ? `hours` : `days`} in a ${dateUnit}`;
  }
}

export function retentionLineChartDescription(query: RetentionQuery) {
  const {retentionUnit} = query;
  const dataGroupName = `${query.getDataGroupName()}s`;

  const alignmentModifier = query.retentionAlignmentType === `birth` ? `` : `calendar `;
  if (query.unboundedMode === `carry_back`) {
    return `X and more ${alignmentModifier}${retentionUnit}s later your ${dataGroupName} came back and did B.`;
  } else if (query.unboundedMode === `carry_forward`) {
    return `X and more ${alignmentModifier}${retentionUnit}s before your ${dataGroupName} came back and did B.`;
  } else if (query.unboundedMode === `consecutive_forward`) {
    return `Returns every ${alignmentModifier}${retentionUnit}s after the birth event.`;
  } else {
    return `The number of exact ${alignmentModifier}${retentionUnit}s later your ${dataGroupName} came back and did B.`;
  }
}

// Some old bookmarks will have missing or deprecated fields, this method will try to apply defaults and correct all fields at once
/** @deprecated - TODO: this should eventually be phased out, you should use a standard util to extract and polyfill only the fields you need
 * Example 1: const myField = RetentionQuery.fromBookmark(bookmarkParams).getMyField()
 * Or do Example 2: const myField = getMyField(bookmarkParams)
 **/
export function getBookmarkWithAppliedDefaults(
  bookmarkParams: Partial<RetentionBookmarkParams>,
): RetentionBookmarkParams {
  return RetentionQuery.createFromBookmark(bookmarkParams).generateBookmark();
}

export function getMetricSegmentRowVal<T extends CellType>(
  isPercentageView: boolean,
  segmentRow: FormattedRow<T>,
  comparisonRow: FormattedRow<T>,
  liftRow?: LiftRow,
): {
  value: number;
  comparisonValue: number;
  liftValue: number;
} {
  let lastCompletedDateCellInd = 0;
  segmentRow.data.forEach((d, ind) => {
    lastCompletedDateCellInd = !d.incomplete ? ind : lastCompletedDateCellInd;
  });
  return {
    value: getRowValue(segmentRow),
    comparisonValue: getRowValue(comparisonRow),
    liftValue: getRowValue(liftRow),
  };

  function getRowValue(row): number {
    if (!row || !row.data[lastCompletedDateCellInd]) {
      return null;
    }

    return isPercentageView ? row.data[lastCompletedDateCellInd].percent : row.data[lastCompletedDateCellInd].value;
  }
}

export function getSegmentColumnSpecs(groupBy: Segment[]): SegmentColumnSpec[] {
  return groupBy.length > 0
    ? groupBy.map((segment) => {
        return {
          title: getGroupByName(segment),
          segmentType: getSegmentPropertyType(segment),
        };
      })
    : [{title: `Property`, segmentType: PropertyType.String}];
}

export function getMetricDataTableRow(
  segmentRow: FormattedRow,
  segmentKeyToPath: SegmentKeyToPath,
  bookmarkParams: RetentionBookmarkParams,
  comparisonRow: FormattedRow,
): TableRow {
  const segmentHeadersValue = {};
  if (isSegmented(bookmarkParams.group_by)) {
    segmentKeyToPath[segmentRow.raw_head]?.forEach((seg, idx) => {
      segmentHeadersValue[`${CommonDataTableFields.SegmentPrefix}${idx}`] = seg;
    });
  } else {
    segmentHeadersValue[`${CommonDataTableFields.SegmentPrefix}0`] = segmentRow.raw_head;
  }
  const {value, comparisonValue} = getMetricSegmentRowVal(bookmarkParams.percentage_view, segmentRow, comparisonRow);
  return {
    identifier: segmentRow.raw_head,
    ...segmentHeadersValue,
    [CommonDataTableFields.Total]: value,
    ...(comparisonRow
      ? {
          [CommonDataTableFields.TimeComparisonTotal]: comparisonValue,
        }
      : {}),
  };
}

export function getMetricDataTableColumns(bookmarkParams: RetentionBookmarkParams): Columns {
  const segmentColumnSpecs = getSegmentColumnSpecs(bookmarkParams.group_by);
  const columns: Columns = segmentColumnSpecs.map((segmentColumnSpec, idx) => ({
    title: segmentColumnSpec.title,
    contentType: segmentColumnSpec.segmentType,
    field: `${CommonDataTableFields.SegmentPrefix}${idx}`,
    type: TableColumnType.Segment,
    sticky: true,
    isSortable: true,
  }));

  const cellRenderer = ({item, context}) =>
    !bookmarkParams.percentage_view
      ? formatDataTableNumber(item[context.field] ?? 0, {isAverage: false})
      : formatPercent(item[context.field] ?? 0);

  columns.push({
    field: CommonDataTableFields.Total,
    type: TableColumnType.Data,
    contentType: TableDataType.Number,
    title: `Total`,
    cellRenderer,
    isSortable: true,
    sticky: true,
    sortSelectTitle: `Value`,
  });

  if (bookmarkParams.time_comparison) {
    columns.push({
      field: CommonDataTableFields.TimeComparisonTotal,
      type: TableColumnType.Data,
      contentType: TableDataType.Number,
      title: `Total (Past)`,
      cellRenderer,
      isSortable: true,
      sticky: true,
    });
  }

  return columns;
}

export function getEventDisplayName(event: string): string {
  let eventName;
  if (event === SPECIAL_EVENTS.ANY_EVENT) {
    eventName = `Anything`;
  } else if (event.includes(`$custom_event`)) {
    const id = parseInt(event.substring(14));
    eventName = customEventsStore.value[id]?.name ?? `Deleted Custom Event`;
  } else {
    eventName = renameEvent(event);
  }
  return eventName;
}
